import React from 'react';
import { Formik, Form } from 'formik';
import axios from 'axios';
import * as yup from 'yup';
import { useNavigate, NavLink, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { Box } from '@mui/material';
import { Button, Grid, Stack, Typography } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import Copyright from '../../Apm/Other/Copyright';
import { numbers_and_letters } from '../../Data/regex';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import { baseURL } from '../../Data/baseURL';
import ButtonLoading from '../../Controls/Buttons/ButtonLoading';

import AuthShell from '../../Public/AuthShell';

const initialValues = {
  new_password: '',
  re_new_password: '',
};

const validationSchema = yup.object({
  new_password: yup
    .string()
    .required(() => i18n.t('yup.required'))
    .min(8, () => i18n.t('yup.password_can_only_contain_latin_letters_numbers'))
    .matches(numbers_and_letters, () => i18n.t('yup.password_can_only_contain_latin_letters_numbers')),
  re_new_password: yup.string().oneOf([yup.ref('new_password'), null], () => i18n.t('yup.password_must_match')),
});

const axiosInst = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

export default function PassResetConfirm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = (e, values) => {
    e.preventDefault();
    setLoading(true);

    const body = {
      new_password: values.new_password.trim(),
      re_new_password: values.re_new_password.trim(),
      uid: uid,
      token: token,
    };
    if (body.new_password === body.re_new_password) {
      axiosInst
        .post('/auth/users/reset_password_confirm/', body)
        .then((res) => {
          if (res.status === 204) {
            navigate('/', { replace: true });
            toast.success(() => i18n.t('toast.log_in_with_new_password'));
            setLoading(false);
          }
        })
        .catch(() => {
          toast.error(() => i18n.t('toast.not_logged_in'), {
            autoClose: 4000,
          });
          setLoading(false);
        });
    } else {
      toast.error(() => i18n.t('toast.password_does_not_match_pls_re_enter'), {
        autoClose: 4000,
      });
      setLoading(false);
    }
  };

  return (
    <>
      <AuthShell mode="reset">
        <Button component={NavLink} to="/" color="primary" size="small" variant="text">
          <Stack direction="row" spacing={1}>
            <KeyboardBackspaceIcon />
            <Typography>{t('button.back_to_main_page')}</Typography>
          </Stack>
        </Button>

        <Typography align="center" variant="h5" sx={{ mt: 10, mb: 2 }}>
          {t('general.enter_new_password')}
        </Typography>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values }) => {
            return (
              <Form>
                <Grid item xs={12}>
                  <TextFieldNormal
                    variant="outlined"
                    required
                    fullWidth
                    label={t('general.new_password')}
                    name="new_password"
                    autoComplete="new-password"
                    type="password"
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <TextFieldNormal
                    variant="outlined"
                    required
                    fullWidth
                    name="re_new_password"
                    label={t('general.confirm_new_password')}
                    type="password"
                    autoComplete="re-new-password"
                  />
                </Grid>
                <ButtonLoading
                  fullWidth
                  loading={loading}
                  variant="contained"
                  color="primary"
                  onClick={(e) => handleSubmit(e, values)}
                  sx={{ mt: 5 }}
                >
                  {t('button.send')}
                </ButtonLoading>
                <Grid container sx={{ mt: 5 }} spacing={1}>
                  <Grid item xs={12} sx={{ mb: 1 }}>
                    <Button component={NavLink} to="/apps/login" color="primary" size="small" variant="text">
                      <Typography>{t('button.already_have_account')}</Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button component={NavLink} to="/apps/signup" color="primary" size="small" variant="text">
                      <Typography>{t('button.do_not_have_account')}</Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>

        <Box sx={{ mt: 8, mb: 5 }}>
          <Copyright />
        </Box>
      </AuthShell>
    </>
  );
}
